import { useQuery } from '@tanstack/react-query';
import {
  fetchAuditLogs,
  AuditLog,
  AuditLogsResponse,
  AuditLogsFilters,
} from '@/api/auditLogs.api';

/**
 * Hook for managing audit logs
 * 
 * Provides:
 * - List of audit logs with pagination
 * - Filtering by action and date range
 * - Loading and error states
 */
export function useAuditLogs(
  workspaceId: string | undefined,
  filters?: AuditLogsFilters
) {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<AuditLogsResponse>({
    queryKey: ['audit-logs', workspaceId, filters],
    queryFn: async () => {
      if (!workspaceId) {
        throw new Error('Workspace ID is required');
      }
      return await fetchAuditLogs(workspaceId, filters);
    },
    enabled: !!workspaceId,
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
  });

  return {
    logs: data?.logs || [],
    total: data?.total || 0,
    limit: data?.limit || 50,
    offset: data?.offset || 0,
    isLoading,
    error,
    refetch,
  };
}
